import { PackageDiscount } from '../../../shared/types';
import { getUserTimezone, parseWallClockDateTimeInTimeZone } from '../../../shared/utils/date.util';
import { formatDiscountAmount } from '../../../discount/utils/discount-display.util';
import {
  ORDER_REQUEST_CUSTOM_DISCOUNT,
  OrderRequestCustomerHistory,
  OrderRequestDiscountCustomSchedule,
  OrderRequestDiscountItem,
} from '../models/notification-order-request.models';

/** Order Request tooltip titles only — API/enum values stay advance_custom / advance_recurring. */
const ORDER_REQUEST_RENEWAL_CYCLE_LABELS: Record<string, string> = {
  advance_custom: 'Custom Schedule',
  advance_recurring: 'Recurring',
};

function mapCustomSchedules(
  rows: PackageDiscount['custom_schedules']
): OrderRequestDiscountCustomSchedule[] {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows
    .filter(row => row != null)
    .map(row => ({
      renewalNumber: Number(row.renewal_number),
      discountType: String(row.discount_type || ''),
      discountValue: row.discount_value,
    }))
    .filter(row => Number.isFinite(row.renewalNumber));
}

export function mapPackageDiscountsToOrderDiscounts(
  rows: PackageDiscount[]
): OrderRequestDiscountItem[] {
  const out: OrderRequestDiscountItem[] = [];
  for (const r of rows) {
    if (r.is_active === false) {
      continue;
    }
    // Order Request only offers shop (normal) discounts; custom rows are per-order.
    if (String(r.type || '').toLowerCase() === 'custom') {
      continue;
    }
    const id = `disc_${r.id}`;
    const dt = (r.discount_type || '').toLowerCase();
    const isPct = dt === 'percentage' || dt === 'percent';
    const val = String(r.discount_value ?? '').trim();
    const num = parseFloat(val.replace(/,/g, ''));
    let fixedPriceDisplay: string;
    if (isPct) {
      if (!Number.isFinite(num)) {
        fixedPriceDisplay = val ? `${val}%` : '—';
      } else {
        fixedPriceDisplay =
          num % 1 === 0 ? `${Math.round(num)}%` : `${parseFloat(num.toFixed(2))}%`;
      }
    } else {
      fixedPriceDisplay = Number.isFinite(num) ? `$${num.toFixed(2)}` : val ? `$${val}` : '—';
    }
    const renewalCycle = String(r.renewal_cycle || 'standard').trim() || 'standard';
    const recurringMonthsRaw = r.recurring_renewal_months;
    const recurringMonths =
      recurringMonthsRaw === null || recurringMonthsRaw === undefined
        ? null
        : Number(recurringMonthsRaw);
    out.push({
      id,
      label: (r.name || 'Discount').trim() || 'Discount',
      mode: 'fixed',
      fixedPrice: fixedPriceDisplay,
      discountType: r.discount_type,
      discountValue: val,
      discountCode: r.code,
      washTypes: Array.isArray(r.wash_types) ? r.wash_types.filter(Boolean) : [],
      eligibility: r.eligibility,
      alwaysActive: r.always_active,
      startDate: r.start_date ?? null,
      endDate: r.end_date ?? null,
      restrictDaysOfWeek: Array.isArray(r.restrict_days_of_week)
        ? r.restrict_days_of_week.filter(Boolean)
        : [],
      restrictFromTime: r.restrict_from_time ?? null,
      restrictToTime: r.restrict_to_time ?? null,
      packageIds: Array.isArray(r.package_ids) ? r.package_ids.filter(Boolean).map(String) : [],
      minimumPurchaseAmount: r.minimum_purchase_amount ?? null,
      renewalCycle,
      recurringRenewalMonths: Number.isFinite(recurringMonths) ? recurringMonths : null,
      customSchedules: mapCustomSchedules(r.custom_schedules),
    });
  }
  return out;
}

/**
 * Info icon on Monthly Membership tiles with advance renewal pricing only.
 * Single Wash (and mixed single+monthly) never show it; standard cycle never does.
 */
export function shouldShowOrderRequestDiscountRenewalInfo(
  item: OrderRequestDiscountItem | null | undefined
): boolean {
  if (!item || item.mode !== 'fixed') {
    return false;
  }
  const cycle = String(item.renewalCycle || 'standard').trim();
  if (cycle !== 'advance_custom' && cycle !== 'advance_recurring') {
    return false;
  }
  const washTypes = Array.isArray(item.washTypes) ? item.washTypes.filter(Boolean) : [];
  return washTypes.includes('monthly_membership') && !washTypes.includes('single_wash');
}

/** Tooltip body from the discount's configured advance renewal fields (plain text + newlines). */
export function formatOrderRequestDiscountRenewalTooltip(
  item: OrderRequestDiscountItem | null | undefined
): string {
  if (!item || !shouldShowOrderRequestDiscountRenewalInfo(item)) {
    return '';
  }
  const cycle = String(item.renewalCycle || '').trim();
  const title = ORDER_REQUEST_RENEWAL_CYCLE_LABELS[cycle] || cycle;

  if (cycle === 'advance_custom') {
    const schedules = Array.isArray(item.customSchedules) ? item.customSchedules : [];
    const lines = [...schedules]
      .sort((a, b) => a.renewalNumber - b.renewalNumber)
      .map(
        schedule =>
          `Renewal ${schedule.renewalNumber}: ${formatDiscountAmount(
            schedule.discountType,
            schedule.discountValue
          )}`
      );
    return [title, ...lines].join('\n');
  }

  const months = item.recurringRenewalMonths;
  const monthsLine =
    months !== null && months !== undefined && Number.isFinite(Number(months))
      ? `Applies for ${months} month${Number(months) === 1 ? '' : 's'}`
      : 'Recurring on every renewal';
  return [title, monthsLine].join('\n');
}

export function buildOrderRequestDiscountList(rows: PackageDiscount[]): OrderRequestDiscountItem[] {
  const mapped = mapPackageDiscountsToOrderDiscounts(rows);
  return [...mapped, ORDER_REQUEST_CUSTOM_DISCOUNT];
}

/**
 * Tiles that apply to the wash type being ordered.
 *
 * A discount with no wash types applies to all of them, which is how the API stores an
 * unrestricted discount. Custom discount is always offered — it is written per order, so
 * no wash type limits it. With no wash type picked yet, nothing is filtered out.
 */
export function filterOrderRequestDiscountsForWashType(
  items: OrderRequestDiscountItem[],
  washType: string | null | undefined
): OrderRequestDiscountItem[] {
  const wanted = String(washType ?? '').trim();
  if (!wanted) {
    return items;
  }
  return items.filter(item => {
    if (item.mode !== 'fixed') {
      return true;
    }
    const washTypes = Array.isArray(item.washTypes) ? item.washTypes.filter(Boolean) : [];
    return washTypes.length === 0 || washTypes.includes(wanted);
  });
}

/** Lowercase weekday tokens, matching discounts.restrict_days_of_week storage. */
const WEEKDAY_TOKENS: readonly string[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

/**
 * Weekday + wall-clock minutes at the shop rather than in UTC.
 *
 * Mirrors resolveLocalDayAndMinutes in svc-order's orderDiscountService: day/time
 * Restriction is the shop's local calendar and clock, so both sides must read the
 * same zone or the tile list and the server would disagree.
 */
function resolveLocalDayAndMinutes(
  date: Date,
  timeZone?: string
): { weekday: string; minutes: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    ...(timeZone ? { timeZone } : {}),
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const lookup: Record<string, string> = {};
  for (const part of parts) {
    lookup[part.type] = part.value;
  }

  const hour = Number(lookup['hour'] === '24' ? '0' : lookup['hour']);
  const minute = Number(lookup['minute']);

  return {
    weekday: String(lookup['weekday'] || '').toLowerCase(),
    minutes: (Number.isFinite(hour) ? hour : 0) * 60 + (Number.isFinite(minute) ? minute : 0),
  };
}

/** 'HH:MM' / 'HH:MM:SS' to minutes past midnight; null when unset or unparseable. */
function timeToMinutes(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const match = String(value)
    .trim()
    .match(/^(\d{1,2}):(\d{2})/);
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return null;
  }
  return hours * 60 + minutes;
}

/**
 * Schedule window. `alwaysActive` defaults to true, so a discount is only bounded when
 * the flag is explicitly false — the same reading svc-order applies.
 *
 * Absolute `start_date` / `end_date` (with `Z`/offset) compare as instants. API wall-clock
 * strings (no `Z`) are parsed in the Order Request Location timezone via
 * {@link parseWallClockDateTimeInTimeZone}, matching svc-order's location-local evaluation.
 */
function isWithinScheduleWindow(
  item: OrderRequestDiscountItem,
  at: Date,
  timeZone?: string
): boolean {
  if (item.alwaysActive !== false) {
    return true;
  }
  const zone = timeZone || getUserTimezone();
  const startsAt = item.startDate ? parseWallClockDateTimeInTimeZone(item.startDate, zone) : null;
  const endsAt = item.endDate ? parseWallClockDateTimeInTimeZone(item.endDate, zone) : null;
  if (startsAt && at < startsAt) {
    return false;
  }
  if (endsAt && at > endsAt) {
    return false;
  }
  return true;
}

/**
 * Day/time Restriction — the configured window is when the discount must NOT show.
 *
 * Current weekday and wall-clock minutes come from the Order Request Location timezone
 * (same zone svc-order loads via locations.timezone), never the browser/server zone.
 *
 * - Restricted day, no From/To → hide for the whole day.
 * - Restricted day, both From and To → hide only while currentTime is in
 *   [from, to] inclusive.
 * - Current day not in `restrict_days_of_week` → Restriction does not block
 *   (From/To are ignored on non-restricted days).
 * - Half-set From/To → treat as no time window (full-day block on a restricted day).
 */
function passesDayAndTimeRestriction(
  item: OrderRequestDiscountItem,
  at: Date,
  timeZone?: string
): boolean {
  const restrictedDays = Array.isArray(item.restrictDaysOfWeek)
    ? item.restrictDaysOfWeek.filter(Boolean).map(day => String(day).toLowerCase())
    : [];

  if (restrictedDays.length === 0) {
    return true;
  }

  const zone = timeZone || getUserTimezone();
  const local = resolveLocalDayAndMinutes(at, zone || undefined);

  if (!WEEKDAY_TOKENS.includes(local.weekday) || !restrictedDays.includes(local.weekday)) {
    return true;
  }

  const fromMinutes = timeToMinutes(item.restrictFromTime);
  const toMinutes = timeToMinutes(item.restrictToTime);
  const hasTimeWindow = fromMinutes !== null && toMinutes !== null;

  // Restricted day with no (valid) From/To → block the entire day.
  if (!hasTimeWindow) {
    return false;
  }

  // Inclusive [from, to]. Overnight wrap is unexpected (create form requires to > from)
  // but handled safely so a bad row does not throw.
  const inRestrictedWindow =
    fromMinutes <= toMinutes
      ? local.minutes >= fromMinutes && local.minutes <= toMinutes
      : local.minutes >= fromMinutes || local.minutes <= toMinutes;

  return !inRestrictedWindow;
}

/**
 * Audience rule (selected plate's order history). An unknown plate is treated as first
 * time, matching the server: there is no history to contradict it.
 */
function matchesCustomerHistory(
  item: OrderRequestDiscountItem,
  history: OrderRequestCustomerHistory | null | undefined
): boolean {
  const eligibility = String(item.eligibility ?? 'all_customer')
    .trim()
    .toLowerCase();
  if (eligibility === 'all_customer' || eligibility === '') {
    return true;
  }
  const resolved = history ?? 'first_time';
  return eligibility === resolved;
}

/**
 * Package scope. Empty packageIds means every package — the same "empty = all" rule
 * svc-order uses when applying a discount. There is no separate is_all_packages flag.
 */
function matchesSelectedPackage(
  item: OrderRequestDiscountItem,
  packageId: string | null | undefined
): boolean {
  const packageIds = Array.isArray(item.packageIds)
    ? item.packageIds.filter(Boolean).map(String)
    : [];
  if (packageIds.length === 0) {
    return true;
  }
  const selected = String(packageId ?? '').trim();
  return Boolean(selected) && packageIds.includes(selected);
}

/**
 * Minimum purchase. Mirrors svc-order: only amounts greater than 0 gate the discount;
 * null / undefined / empty / 0 do not block. Equality (`orderAmount === minimum`) passes.
 */
function meetsMinimumPurchase(
  item: OrderRequestDiscountItem,
  orderAmount: number | null | undefined
): boolean {
  const raw = item.minimumPurchaseAmount;
  if (raw === null || raw === undefined || raw === '') {
    return true;
  }
  const minimum = Math.round((Number(raw) || 0) * 100) / 100;
  if (!(minimum > 0)) {
    return true;
  }
  const amount = Math.max(0, Math.round((Number(orderAmount) || 0) * 100) / 100);
  return amount >= minimum;
}

/** Tiles that are not a shop discount row — today just Custom discount. */
function nonShopDiscountTiles(items: OrderRequestDiscountItem[]): OrderRequestDiscountItem[] {
  return items.filter(item => item.mode !== 'fixed');
}

/**
 * Tiles a given order may actually use.
 *
 * Applies the same gates svc-order enforces on submit — wash type, package scope,
 * audience, schedule window, day/time restriction and minimum purchase — so staff
 * never pick a tile the API will reject. Custom discount is always offered: it is
 * written per order and no discounts row limits it.
 *
 * Until a wash type is known the order has nothing to match a shop discount against, so
 * only Custom discount is offered. Showing every discount up front would advertise ones
 * the order may turn out not to qualify for, and would make the list shrink as staff
 * work rather than grow.
 *
 * Hiding rather than disabling matches how wash-type filtering already behaves.
 */
export function filterOrderRequestDiscountsForOrder(
  items: OrderRequestDiscountItem[],
  options: {
    washType?: string | null;
    packageId?: string | null;
    customerHistory?: OrderRequestCustomerHistory | null;
    at?: Date;
    timeZone?: string;
    /** Package list price + add-ons (pre-discount), matching svc-order's baseAmount. */
    orderAmount?: number | null;
  } = {}
): OrderRequestDiscountItem[] {
  const washType = String(options.washType ?? '').trim();
  if (!washType) {
    return nonShopDiscountTiles(items);
  }

  const at = options.at ?? new Date();
  const byWashType = filterOrderRequestDiscountsForWashType(items, washType);

  return byWashType.filter(item => {
    if (item.mode !== 'fixed') {
      return true;
    }
    return (
      matchesSelectedPackage(item, options.packageId) &&
      matchesCustomerHistory(item, options.customerHistory) &&
      isWithinScheduleWindow(item, at, options.timeZone) &&
      passesDayAndTimeRestriction(item, at, options.timeZone) &&
      meetsMinimumPurchase(item, options.orderAmount)
    );
  });
}
